import { getCardsFromStr, getAllCards } from '@/helpers/cards'
import {
  getCardsValues,
  shortcutTypes,
  mttHeroStackTypes,
  getHandsCategoriesOptions,
  getDrawsCategoriesOptions,
  getGroupsSelectionOptions,
} from '@/helpers/constants'
import { rangeTableGroups } from '@/helpers/solution'
import { trainerDefaultPreset, trainerMustDefaultPreset } from '@/helpers/trainer_presets'

function getBoardFilterOptions() {
  const res = {
    flop_suits: ['rainbow', 'flush_draw', 'monotone'],
    flop_paired: ['not_paired', 'paired', 'tripled'],
    flop_connectedness: ['disconnected', 'oesd_possible', 'connected'],
    flop_subset: ['25', '49', '85', '184'],
    flop_high_card: getCardsValues(),
    flop_mid_card: getCardsValues(),
    flop_low_card: getCardsValues(),
    turn_suit: ['rainbow', 'backdoor', 'flush'],
    turn_paired: ['not_paired', 'paired'],
    turn_card: ['high', 'mid', 'low'],
    river_suit: ['rainbow', 'backdoor', 'flush'],
    river_paired: ['not_paired', 'paired'],
    river_card: ['high', 'mid', 'low'],
  }
  return res
}
function getFullHandPreferencesOptions() {
  // can be changed during the game
  const on = ['on']
  const res = {
    fh_trainer_mode: ['stop_end_of_hand', 'stop_end_of_street', 'stop_after_action'],
    fh_trainer_game_mode: ['trainer_actions', 'trainer_frequency'],
    fh_trainer_grouping: ['swv_grouping_none', 'swv_grouping_4', 'swv_grouping_all'],
    fh_trainer_result: ['after_mistake', 'after_action'],
    fh_trainer_autoplay: ['3_sec', '0_sec'],
    fh_trainer_timebank:
      import.meta.env.VITE_BRANCH === 'master'
        ? ['7_sec', '15_sec', '25_sec']
        : ['1_sec', '2_sec', '7_sec', '9999_sec'],
    fh_trainer_game_speed: ['normal', 'fast', 'turbo'],
    fh_trainer_hero_frequency: on,
    fh_trainer_quick_result: on,
    fh_trainer_learning_mode: on,
    fh_trainer_hero_range: on,
    fh_trainer_opponent_range: on,
    fh_trainer_equity_chart: on,
    fh_trainer_ranges_comparison: on,
    fh_trainer_hand_strength: on,
    fh_trainer_total_frequency: on,
    fh_trainer_category_hands: on,
    fh_trainer_category_draws: on,
    fh_trainer_hero_strategy: on,
    fh_trainer_rng_visible: on,
    fh_trainer_streak_visible: on,
    // fh_trainer_sounds: on,
    fh_rng: ['low', 'high'],
    fh_trainer_session:
      import.meta.env.VITE_BRANCH === 'master'
        ? ['20', '50', '100', '200', '420']
        : ['2', '3', '4', '5', '10', '20', '50', '100', '200', '420'],
  }
  return res
}
function getFullHandGameOptions({ hasTmp = true, availablePositions = null } = {}) {
  const allPlayers = ['UTG', 'UTG+1', 'LJ', 'HJ', 'CO', 'BTN', 'SB', 'BB', 'STR']
  const on = ['on']
  const tmp = []
  const res = {
    fh_start_spot: ['preflop', 'flop', 'custom_spot'],
    fh_hero: availablePositions || allPlayers,
    fh_opponent: availablePositions || allPlayers,
    fh_hero_stack_type: mttHeroStackTypes(),
    fh_alternate: on,
    fh_actions: shortcutTypes(),
    fh_rel_positions: ['IP', 'OOP'],

    fh_groups_selection: getGroupsSelectionOptions(),
    fh_groups: rangeTableGroups(),
    fh_hands: getHandsCategoriesOptions(),
    fh_draws: getDrawsCategoriesOptions(),

    fh_trainer_tables: ['1', '2', '3', '4'],

    ...(hasTmp
      ? {
          fh_hero_tmp: tmp,
          fh_opponent_tmp: tmp,
          fh_action_tmp: tmp,
          fh_rel_position_tmp: tmp,
        }
      : {}),
  }
  // console.log('availablePositions', availablePositions)
  return res
}
function getFullHandSetupOptions({ availablePositions = null } = {}) {
  const res = {
    ...getFullHandGameOptions({ availablePositions }),
    ...getFullHandPreferencesOptions(),
  }
  return res
}

// ----------------

function sanitizeQuery(q) {
  Object.keys(q).forEach(key => {
    const str = q[key]?.toString()
    if (!str) {
      delete q[key]
    } else {
      q[key] = str
    }
  })
  return q
}
function getDefaultQuery(conf) {
  const res = {}
  Object.keys(conf).forEach(key => {
    res[key] = conf[key].default
  })
  return res
}
function sanitizeBoard(cards) {
  cards = [...new Set(cards)]
  const allCards = getAllCards()
  cards = cards.filter(c => allCards.some(cId => cId === c))
  if (cards.length < 3) {
    cards = []
  }
  return cards
}
function getJSONFromLocalStorage(lsKey) {
  let res = localStorage.getItem(lsKey)
  try {
    res = JSON.parse(res)
  } catch {
    res = null
  }
  return res
}
function geUrlObject() {
  let res = new URL(document.location).searchParams
  res = Object.fromEntries(res)
  return res
}
function getLastKnownQuery(lsKey, localOnly, wantedUrlKeys) {
  const urlQuery = geUrlObject()

  // only if url is empty >> fallback to localstorage
  const urlPopulated = wantedUrlKeys
    ? wantedUrlKeys.some(k => k in urlQuery)
    : Object.keys(urlQuery).length

  const localQuery = localOnly || !urlPopulated ? getJSONFromLocalStorage(lsKey) : {}

  const res = {
    ...localQuery,
    ...(localOnly ? {} : urlQuery),
  }
  return res
}
function getCheckedQuery({ options, mustHaves = {}, query }) {
  const res = {}
  Object.keys(options).forEach(key => {
    let vals = query?.[key]
    if (vals) {
      vals = [...new Set(vals.split(',').filter(i => i))]
      vals = vals.filter(val => options[key].some(v => v === val))
      vals = vals.join(',')
      res[key] = vals
    } else {
      res[key] = mustHaves[key] || ''
    }
  })
  return res
}

// ---------------
function getFullHandSetupConfig() {
  const res = getFullHandSetupOptions()
  const defaults = trainerDefaultPreset()
  Object.keys(res).forEach(key => {
    res[key] = {
      options: res[key],
      default: defaults[key] || '',
    }
  })
  return res
}
function getSanitizedBoardFromString(board) {
  let cards = getCardsFromStr(board)
  cards = sanitizeBoard(cards)
  const res = cards.join('')
  return res
}
function getBoardQuery({ lsKey, key = 'board', localOnly = false, customQuery }) {
  let res = {}
  const query = customQuery || getLastKnownQuery(lsKey, localOnly)

  let board = query?.[key] || ''
  board = getSanitizedBoardFromString(board)
  res = {
    [key]: board,
  }
  return res
}
function getHistoryQuery({ lsKey, localOnly = false, customQuery }) {
  const wantedUrlKeys = [
    'preflop_actions',
    'flop_actions',
    'turn_actions',
    'river_actions',
    'history_spot',
  ]
  const query = customQuery || getLastKnownQuery(lsKey, localOnly, wantedUrlKeys)

  const skipPreflop = query.solution_type === 'custom' //

  const preflop = query.preflop_actions
  const flop = skipPreflop || preflop ? query.flop_actions : ''
  const turn = flop ? query.turn_actions : ''
  const river = turn ? query.river_actions : ''
  const res = {
    preflop_actions: preflop || '',
    flop_actions: flop || '',
    turn_actions: turn || '',
    river_actions: river || '',
    history_spot: query.history_spot || '0',
  }
  return res
}
function checkBoardFilterOptions({ options, lsKey, localOnly, customQuery }) {
  const query = customQuery || getLastKnownQuery(lsKey, localOnly) || {}
  const res = getCheckedQuery({ options, query })
  return res
}
function checkFullHandSetupOptions({ options, lsKey, localOnly, customQuery }) {
  const query = customQuery || getLastKnownQuery(lsKey, localOnly) || {}
  const mustHaves = trainerMustDefaultPreset()
  const res = getCheckedQuery({ options, mustHaves, query })
  return res
}
function getBoardFilterQuery({ lsKey, localOnly = false, customQuery }) {
  const options = getBoardFilterOptions()
  let res = checkBoardFilterOptions({ options, lsKey, localOnly, customQuery })
  res = sanitizeQuery(res)
  return res
}
function getFullHandSetupQuery({ lsKey, localOnly = false, customQuery, availablePositions }) {
  const options = getFullHandSetupOptions({ availablePositions })
  const res = checkFullHandSetupOptions({ options, lsKey, localOnly, customQuery })
  return res
}
function getDialogsQuery() {
  const params = new URL(document.location).searchParams
  let dialogs = params.get('dialogs')

  const closeOnStart = ['cards-dialog', 'practice-filter-dialog', 'history-filter-dialog']
  // 'history-events-dialog',

  dialogs = (dialogs || '').split(',')
  dialogs = dialogs.filter(d => !closeOnStart.includes(d))
  dialogs = [...new Set(dialogs)]
  dialogs = dialogs.join(',')
  let res = {
    dialogs,
  }
  res = sanitizeQuery(res)
  return res
}
function getSoltabQuery(hasReportsFlag_CR_REPORTS) {
  const params = new URL(document.location).searchParams
  const soltab = params.get('soltab')
  let res = {
    soltab,
    ...(hasReportsFlag_CR_REPORTS
      ? { gmfs_solution_tab: soltab === 'reports' ? 'ai_reports' : 'ai_sols' }
      : {}),
  }
  res = sanitizeQuery(res)
  return res
}

function getCustomSolutionQuery({ lsKey, localOnly = false, customQuery }) {
  const wantedUrlKeys = [
    'cussol_id', //
    'custree_id',
    'solution_type',
    'gmfs_solution_tab',
  ]
  const query = customQuery || getLastKnownQuery(lsKey, localOnly, wantedUrlKeys)
  // custom_solution_id custom_tree_id are used in trainer in game,
  // sometimes people dont copy the whole url

  const cussol_id = query.cussol_id || query.custom_solution_id || ''
  const custree_id = query.custree_id || query.custom_tree_id || ''

  let solution_type = query.solution_type || ''
  const solutionTypes = ['gwiz', 'custom']
  solution_type = solutionTypes.includes(solution_type) ? solution_type : 'gwiz'

  let gmfs_solution_tab = query.gmfs_solution_tab || ''
  const tabOptions = ['ai_sols', 'ai_trees', 'ai_reports']
  gmfs_solution_tab = tabOptions.includes(gmfs_solution_tab) ? gmfs_solution_tab : 'ai_sols'

  let res = {
    cussol_id,
    custree_id,
    solution_type,
    gmfs_solution_tab,
  }
  res = sanitizeQuery(res)
  return res
}

function getRouteNameByStudyTab(router, rootGetters) {
  const pathName = router.currentRoute.value.name
  return rootGetters.user_has_flag('CR_REPORTS') &&
    pathName === 'StudySolutions' &&
    rootGetters.sw_tabs_study === 'reports'
    ? 'StudyReports'
    : pathName
}

export {
  sanitizeQuery,
  getBoardQuery,
  getHistoryQuery,
  getBoardFilterQuery,
  getBoardFilterOptions,
  getDialogsQuery,
  getFullHandSetupQuery,
  getSoltabQuery,
  getFullHandSetupOptions,
  getDefaultQuery,
  getFullHandSetupConfig,
  getFullHandPreferencesOptions,
  getFullHandGameOptions,
  sanitizeBoard,
  getCustomSolutionQuery,
  getSanitizedBoardFromString,
  getRouteNameByStudyTab,
}
